import { observer } from "mobx-react-lite"
import { useStores } from "../../../../../../../models/helpers/useStores"
import { getStripeExternalSubscriptionManageUrl } from "../../../../../../../services/firebase-functions/billing"
import styles from "./styles.module.scss"
import { formatCurrency } from "@cruisen/i18n"
import { match } from "ts-pattern"
import { format, differenceInDays } from "date-fns"
import { useMutation } from "@tanstack/react-query"

const TrialSubscription = ({
  trialStart,
  trialEnd,
  price,
  discountedPrice,
  cancelAtPeriodEnd,
  cancelAt,
}: {
  trialStart: number
  trialEnd: number
  price: number
  discountedPrice?: number
  cancelAtPeriodEnd: boolean
  cancelAt: number
}) => {
  return (
    <div>
      <div className={styles.subscriptionHeader}>
        <p className={styles.statusName}>{differenceInDays(trialEnd, trialStart)}-day trial</p>
        <p className={styles.subscriptionPrice}>{formatCurrency(0)}</p>
      </div>
      {cancelAtPeriodEnd ? (
        <p className={styles.subscriptionDescription}>
          Cancels on {format(new Date(cancelAt), "MMMM d")}
        </p>
      ) : (
        <p className={styles.subscriptionDescription}>
          Ends on {format(new Date(trialEnd), "MMMM d")}. Later renews at{" "}
          {formatCurrency(discountedPrice || price)}/month.
        </p>
      )}
    </div>
  )
}

const getDiscountDurationText = (months: number) => {
  if (months > 12) {
    return "more than 1 year"
  }
  if (months === 12) {
    return "1 year"
  }
  if (months > 1) {
    return `${months} months`
  }
  return "1 month"
}

const ActiveSubscription = ({
  nextPaymentDate,
  price,
  discountedPrice,
  discountPercent,
  discountDurationInMonths,
  cancelAtPeriodEnd,
  cancelAt,
}: {
  nextPaymentDate: number
  price: number
  discountedPrice?: number
  discountPercent?: number
  discountDurationInMonths?: number
  cancelAtPeriodEnd: boolean
  cancelAt: number
}) => {
  return (
    <div>
      <div className={styles.subscriptionHeader}>
        <div className={styles.discountRow}>
          <p className={styles.statusName}>Active</p>
          {discountPercent ? (
            <div className={styles.activeDiscount}>
              <p>{discountPercent}% discount applied</p>
            </div>
          ) : null}
        </div>
        <p className={styles.subscriptionPrice}>{formatCurrency(discountedPrice || price)}/month</p>
      </div>
      {cancelAtPeriodEnd ? (
        <p className={styles.subscriptionDescription}>
          Cancels on {format(new Date(cancelAt), "MMMM d")}
        </p>
      ) : (
        <p className={styles.subscriptionDescription}>
          Next payment on {format(new Date(nextPaymentDate), "MMMM d")}.
          {discountPercent && discountDurationInMonths
            ? ` Your ${discountPercent}% discount is valid for ${getDiscountDurationText(
                discountDurationInMonths,
              )}. After that, it renews at ${formatCurrency(price)}/month.`
            : null}
        </p>
      )}
    </div>
  )
}

const PastDueSubscription = () => {
  return (
    <div>
      <div className={styles.subscriptionHeader}>
        <p className={styles.statusName}>Payment failed</p>
        <p className={styles.subscriptionPrice}>$-</p>
      </div>
      <p className={styles.subscriptionDescription}>
        Your latest payment has failed. Update your payment method to continue this subscription.
      </p>
    </div>
  )
}

const ExpiredSubscription = () => {
  return (
    <div>
      <div className={styles.subscriptionHeader}>
        <p className={styles.statusName}>Expired</p>
        <p className={styles.subscriptionPrice}>$-</p>
      </div>
      <p className={styles.subscriptionDescription}>We were unable to renew your subscription.</p>
    </div>
  )
}

export const BillingTab = observer(() => {
  const rootStore = useStores()
  const { mutate: goToManageSubscription, isPending } = useMutation({
    mutationFn: () => getStripeExternalSubscriptionManageUrl(),
    onSuccess: (subscriptionManageUrl) => {
      if (subscriptionManageUrl) {
        window.open(subscriptionManageUrl, "_blank").focus()
      }
    },
  })

  if (!rootStore.stripeSubscription) {
    // This will happen only on dev mode
    return <p className={styles.subscriptionErrorText}>No subscription found</p>
  }

  return (
    <div className={styles.subscriptionDetailsContainer}>
      <div className={styles.sectionInfo}>
        <p className={styles.sectionTitle}>Subscription</p>
        <p className={styles.sectionDescription}>Manage your subscription details</p>
      </div>
      <div className={styles.subscriptionDetails}>
        {match(rootStore.stripeSubscription.status)
          .with("trialing", () => (
            <TrialSubscription
              trialStart={rootStore.stripeSubscription.trialStart}
              trialEnd={rootStore.stripeSubscription.trialEnd}
              price={rootStore.stripeSubscription.price}
              discountedPrice={rootStore.stripeSubscription.discountPrice}
              cancelAtPeriodEnd={rootStore.stripeSubscription.cancelAtPeriodEnd}
              cancelAt={rootStore.stripeSubscription.cancelAt}
            />
          ))
          .with("active", () => (
            <ActiveSubscription
              nextPaymentDate={rootStore.stripeSubscription.currentPeriodEnd}
              price={rootStore.stripeSubscription.price}
              discountPercent={rootStore.stripeSubscription.discountPercentOff}
              discountedPrice={rootStore.stripeSubscription.discountPrice}
              discountDurationInMonths={rootStore.stripeSubscription.discountDurationInMonths}
              cancelAtPeriodEnd={rootStore.stripeSubscription.cancelAtPeriodEnd}
              cancelAt={rootStore.stripeSubscription.cancelAt}
            />
          ))
          .with("past_due", () => <PastDueSubscription />)
          .with("unpaid", "incomplete_expired", "paused", () => <ExpiredSubscription />)
          .otherwise(() => null)}
        {/* If negative, the customer has credit to apply to their next invoice. */}
        {rootStore.stripeSubscription.balance < 0 ? (
          <p className={styles.subscriptionDescription}>
            A {formatCurrency(Math.abs(rootStore.stripeSubscription.balance ?? 0))} credit will be
            applied to the next invoice.
          </p>
        ) : null}
        <button
          onClick={() => goToManageSubscription()}
          disabled={isPending}
          className={styles.manageSubscriptionButton}
        >
          {isPending ? "Loading..." : "Manage my subscription"}
        </button>
      </div>
    </div>
  )
})
